import { useTranslation } from "react-i18next";

import { Box, Checkbox } from "@mui/material";

import { zodResolver } from "@hookform/resolvers/zod";

import { useEffect, useRef, useState } from "react";

import { useForm } from "react-hook-form";

import { Link, useLocation } from "react-router-dom";

import FormContainer from "@/pages/Public/components/FormContainer";
import FormTextField from "@/pages/Private/pages/Account/components/FormTextField";

import { useRequestContactMutation } from "@/pages/Public/redux/contact/contact.api";
import { ROUTE_CONFIG } from "@/routes/config";

import { ContactUsSchema, ContactUsType } from "../schema/contact";

export default function LandingContactSection() {
	const { t } = useTranslation();
	const ts = (key: string) => t(`questionnaire.landingContactSection.${key}`);
	const [error, setError] = useState<string | null>(null);

	const location = useLocation();

	const sectionRef = useRef<HTMLDivElement>(null);

	const [isSuccess, setIsSuccess] = useState<boolean>(false);

	const [requestContact, { isLoading, error: reqError }] = useRequestContactMutation();
	const {
		register,
		handleSubmit,
		formState: { errors, isValid },
		reset,
	} = useForm<ContactUsType>({
		defaultValues: {
			name: "",
			email: "",
			message: "",
			privacyPolicy: false,
		},
		resolver: zodResolver(ContactUsSchema),
	});

	const onSubmit = async (values: ContactUsType) => {
		try {
			await requestContact(values).unwrap();
			setIsSuccess(true);
			reset();
			setError(null);
			setTimeout(() => {
				setIsSuccess(false);
			}, 3000);
		} catch (err) {
			console.error(err);
		}
	};

	useEffect(() => {
		if (sectionRef.current && location.hash === "#contact-us") {
			sectionRef.current.scrollIntoView({ behavior: "smooth" });
		}
	}, [location]);

	return (
		<section ref={sectionRef} className="w-full px-4 md:px-6 lg:px-8" id="contact-us">
			<div className="w-full max-w-[1420px] mx-auto my-16 sm:my-[7.5rem] text-center  px-4 md:px-16">
				<div className="flex flex-col lg:flex-row justify-center  gap-12 lg:gap-[56px]">
					<div className="w-full lg:max-w-[694px] text-left flex flex-col gap-[40px]">
						<div className="flex flex-col gap-[16px]">
							<p className="text-[40px] font-semibold leading-[120%] font-urbanist">
								{ts("title")}
							</p>
							<p className="text-[18px] leading-[150%] font-urbanist font-normal text-subtle">
								{ts("description")}
							</p>
						</div>

						<div className="w-full h-full flex items-center justify-center">
							<FormContainer
								description={ts("form.description")}
								disableSubmitButton={!isValid}
								formContainerClassName="w-full h-full mt-0"
								formError={reqError ?? error}
								formFooter={
									<Box sx={{ display: "flex", alignItems: "center" }}>
										<Checkbox
											sx={errors?.privacyPolicy?.message ? { color: "red" } : {}}
											{...register("privacyPolicy")}
										/>

										<span className="text-xs sm:text-sm text-primary-gray-lighter inline float-left">
											{t("basics.privacyStatementObject.noLink")}
											<Link
												className="text-progress-bar mr-1 font-normal inline"
												rel="noreferrer noopener"
												target="_blank"
												to={ROUTE_CONFIG.PRIVACY}
											>
												{t("basics.privacyStatementObject.link1")}
											</Link>
											{t("basics.privacyStatementObject.noLink2")}
										</span>
									</Box>
								}
								handleSubmit={handleSubmit(onSubmit)}
								isLoading={isLoading}
								submitButtonText={ts("form.confirm")}
								title={ts("form.title")}
							>
								<FormTextField
									autoComplete="name"
									errorMessage={errors?.name?.message}
									formRegister={register("name")}
									label={ts("name")}
									placeHolder={ts("namePlaceholder")}
								/>
								<FormTextField
									autoComplete="email"
									errorMessage={errors?.email?.message}
									formRegister={register("email")}
									label={ts("email")}
									placeHolder={ts("emailPlaceholder")}
								/>
								<FormTextField
									autoComplete="message"
									errorMessage={errors?.message?.message}
									formRegister={register("message")}
									label={ts("message")}
									multiline={true}
									placeHolder={ts("messagePlaceholder")}
								/>
							</FormContainer>
						</div>
						{isSuccess && (
							<div className="flex flex-col gap-[16px]">
								<p className="text-[18px] text-center leading-[150%] font-urbanist font-normal text-subtle">
									{ts("success")}
								</p>
							</div>
						)}
					</div>
				</div>
			</div>
		</section>
	);
}
